import React, { Fragment, useEffect, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { getComboConcepto } from '../../../api/api-implicado-client'

// «Datos adicionales de la víctima» (RG-576, prototipo del informe de defensa penal): sección de la pestaña Implicado del modal de
// ingreso y actualización de implicados. Se guarda en implicado_persona del implicado (idInvolucrado 1) y la edad se calcula a la
// fecha del siniestro (no se guarda). Sigue el patrón del resto del modal: el valor vigente se deja en `formik.values`.

// Conceptos de maestros (concepto_detalle) de cada combo.
const CONCEPTOS = { sexo: 356, estadoCivil: 357, estrato: 358, educacion: 359, ocupacion: 360 }
const MAX_HOSPITAL = 100

const hoyISO = () => {
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

// Años cumplidos a la fecha del siniestro (mismo cálculo que TIMESTAMPDIFF(YEAR, …) de MySQL). null si falta una fecha o si la
// persona aún no había nacido a esa fecha.
export const edadAlSiniestro = (fechaNacimiento, fechaSiniestro) => {
  const iso = /^\d{4}-\d{2}-\d{2}$/
  if (!iso.test(fechaNacimiento || '') || !iso.test(fechaSiniestro || '')) return null
  const [ny, nm, nd] = fechaNacimiento.split('-').map(Number)
  const [sy, sm, sd] = fechaSiniestro.split('-').map(Number)
  const edad = sy - ny - ((sm < nm || (sm === nm && sd < nd)) ? 1 : 0)
  return edad >= 0 ? edad : null
}

const aTexto = (v) => (v === null || v === undefined ? '' : String(v))

const estiloEtiqueta = { fontWeight: 'bold', fontSize: '14px' }

const DatosAdicionalesVictima = ({ formik, isImplicado, handleShowProp }) => {
  const { datosPantalla } = useSelector((state) => state.implicadoReducer)
  const { antecedentesCasos } = useSelector((state) => state.casoGeneralReducer)
  const fechaSiniestro = antecedentesCasos?.antecedentesCasos?.[0]?.FechaSiniestroFmt

  const [combos, setCombos] = useState({ sexo: [], estadoCivil: [], estrato: [], educacion: [], ocupacion: [] })
  const [sexo, setSexo] = useState('')
  const [estadoCivil, setEstadoCivil] = useState('')
  const [estrato, setEstrato] = useState('')
  const [educacion, setEducacion] = useState('')
  const [ocupacion, setOcupacion] = useState('')
  const [fechaNacimiento, setFechaNacimiento] = useState('')
  const [hospitalClinica, setHospitalClinica] = useState('')

  // Los maestros no cambian entre aperturas: se piden una vez al montar la sección.
  useEffect(() => {
    let vigente = true
    Promise.all(Object.keys(CONCEPTOS).map((clave) =>
      getComboConcepto(CONCEPTOS[clave]).then((lista) => [clave, lista || []]).catch(() => [clave, []])
    )).then((pares) => {
      if (!vigente) return
      const nuevos = {}
      pares.forEach(([clave, lista]) => { nuevos[clave] = lista })
      setCombos(nuevos)
    })
    return () => { vigente = false }
  }, [])

  // Cada apertura del modal parte en blanco (igual que el resto de la pestaña Implicado).
  useEffect(() => {
    setSexo(''); setEstadoCivil(''); setEstrato(''); setEducacion(''); setOcupacion('')
    setFechaNacimiento(''); setHospitalClinica('')
  }, [handleShowProp])

  // Al actualizar un implicado existente se precargan los datos guardados.
  useEffect(() => {
    const d = datosPantalla?.datosPantalla?.[0]
    if (isImplicado || !d) return
    setSexo(aTexto(d['SexoImplicado']))
    setEstadoCivil(aTexto(d['EstadoCivilImplicado']))
    setEstrato(aTexto(d['EstratoImplicado']))
    setEducacion(aTexto(d['EducacionImplicado']))
    setOcupacion(aTexto(d['OcupacionImplicado']))
    setFechaNacimiento(aTexto(d['FechaNacimientoImplicado']))
    setHospitalClinica(aTexto(d['HospitalClinicaImplicado']))
  }, [datosPantalla, isImplicado])

  // Valores que lee el envío del modal (ver `data.implicado` en implicadoTerceros/index.js).
  formik.values.sexoImplicado = sexo
  formik.values.estadoCivilImplicado = estadoCivil
  formik.values.estratoImplicado = estrato
  formik.values.educacionImplicado = educacion
  formik.values.ocupacionImplicado = ocupacion
  formik.values.fechaNacimientoImplicado = fechaNacimiento
  formik.values.hospitalClinicaImplicado = hospitalClinica

  const edad = edadAlSiniestro(fechaNacimiento, fechaSiniestro)

  const combo = (nombre, etiqueta, valor, setValor, lista) => (
    <Fragment>
      <Form.Label className='mt-3 mb-0' style={estiloEtiqueta}>{etiqueta}</Form.Label>
      <Form.Select className='mt-2' name={nombre} value={valor}
        onChange={(e) => { formik.handleChange(e); setValor(e.target.value) }}>
        <option value=''>---</option>
        {lista.map((c) => <option key={c.id} value={String(c.id)}>{c.nombre}</option>)}
      </Form.Select>
    </Fragment>
  )

  return (
    <Fragment>
      <hr className='mt-4 mb-1' />
      <p className='mb-0' style={{ fontWeight: 'bold', fontSize: '16px', color: '#4F517A' }}>Datos adicionales de la víctima</p>
      <Row>
        <Col md='3'>{combo('sexoImplicado', 'Sexo', sexo, setSexo, combos.sexo)}</Col>
        <Col md='3'>{combo('estadoCivilImplicado', 'Estado Civil', estadoCivil, setEstadoCivil, combos.estadoCivil)}</Col>
        <Col md='3'>{combo('estratoImplicado', 'Estrato', estrato, setEstrato, combos.estrato)}</Col>
        <Col md='3'>{combo('educacionImplicado', 'Educación', educacion, setEducacion, combos.educacion)}</Col>
      </Row>
      <Row className='mb-3'>
        <Col md='4'>{combo('ocupacionImplicado', 'Ocupación', ocupacion, setOcupacion, combos.ocupacion)}</Col>
        <Col md='3'>
          <Form.Label className='mt-3 mb-0' style={estiloEtiqueta}>Fecha Nacimiento</Form.Label>
          <Form.Control className='mt-2' type='date' name='fechaNacimientoImplicado' value={fechaNacimiento} max={hoyISO()}
            onChange={(e) => { formik.handleChange(e); setFechaNacimiento(e.target.value) }} />
          {formik.errors.fechaNacimientoImplicado ? (
            <Form.Text variant='danger' style={{ color: 'red' }}>{formik.errors.fechaNacimientoImplicado}</Form.Text>
          ) : ''}
        </Col>
        <Col md='2'>
          <Form.Label className='mt-3 mb-0' style={estiloEtiqueta}>Edad al siniestro</Form.Label>
          <Form.Control className='mt-2 text-center' readOnly disabled value={edad === null ? '—' : `${edad} años`} />
        </Col>
        <Col md='3'>
          <Form.Label className='mt-3 mb-0' style={estiloEtiqueta}>Hospital / Clínica</Form.Label>
          <Form.Control className='mt-2' name='hospitalClinicaImplicado' value={hospitalClinica} maxLength={MAX_HOSPITAL}
            placeholder='Nombre del establecimiento' autoComplete='off'
            onChange={(e) => { formik.handleChange(e); setHospitalClinica(e.target.value) }} />
          {formik.errors.hospitalClinicaImplicado ? (
            <Form.Text variant='danger' style={{ color: 'red' }}>{formik.errors.hospitalClinicaImplicado}</Form.Text>
          ) : ''}
        </Col>
      </Row>
    </Fragment>
  )
}

export default DatosAdicionalesVictima
